import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import { InfoIcon } from './ui'
import { PrimaryButton } from './ui'
import { useApi } from '../services/api'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import type { PolicyLink } from '../services/types'
import { clientLogError } from '../utils/clientLog'

interface PolicyCardProps {
  regoNumber: string
  vehicleName: string
  renewalDate: string
}

function PolicyCard({ regoNumber, vehicleName, renewalDate }: PolicyCardProps) {
  return (
    <div className="bg-background-primary border border-border-primary border-solid flex flex-col isolate items-start relative rounded-2xl shadow-xs w-full">
      <div className="flex flex-col gap-[16px] items-start px-[16px] py-[20px] relative shrink-0 w-full z-[1]">
        <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
          {/* Rego Number Badge */}
          <div className="content-start flex flex-wrap gap-[4px] items-start relative shrink-0 w-full">
            <div className="bg-background-primary border border-text-primary-900 border-solid flex items-center px-[8px] py-[4px] relative rounded-xs shrink-0">
              <p className="font-body font-medium text-md leading-md text-text-primary-900 text-center">
                {regoNumber}
              </p>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="flex flex-col gap-[4px] items-start relative shrink-0 w-full">
            <div className="flex gap-[8px] items-center relative shrink-0 w-full">
              <p className="flex-1 font-body font-semibold text-md leading-md text-text-primary-900 min-h-px min-w-px relative shrink-0">
                {vehicleName}
              </p>
            </div>
            <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm relative shrink-0 text-text-secondary-700 text-sm w-full">
              <p className="font-body font-regular relative shrink-0">
                Renews on{' '}
              </p>
              <p className="font-body font-medium relative shrink-0">
                {renewalDate}
              </p>
            </div>
          </div>
        </div>
      </div>
          </div>
  )
}

export default function MatchFound() {
  const navigate = useNavigate()
  const api = useApi()
  const [policies, setPolicies] = useState<PolicyCardProps[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  useScrollToTopOnErrorSignal(error)

  const handleGetStarted = () => {
    // Navigate to the dashboard (onboarding tutorial shows automatically for first-time users)
    navigate('/dashboard')
  }

  useEffect(() => {
    let cancelled = false

    const fetchPolicies = async () => {
      try {
        setLoading(true)
        const response = await api.getPolicies()
        if (cancelled) return

        const rawPolicies = response.policies ?? []

        // Map API fields to PolicyCard props
        const mapped = rawPolicies.map((p: PolicyLink) => ({
          regoNumber: p.registrationNumber || p.policyNumber || p.policyRef,
          vehicleName: p.vehicleName || `Policy ${p.policyRef}`,
          renewalDate: p.renewalDateFormatted || p.renewalDate || 'Date unavailable'
        }))

        setPolicies(mapped)
      } catch (err) {
        if (cancelled) return
        clientLogError('[MatchFound] Failed to load policies.', err)
        setError(err instanceof Error ? err.message : 'Failed to load policies')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchPolicies()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="bg-background-brand-primary flex flex-col items-center justify-center min-h-screen w-full">
      <main id="main" className="flex w-full flex-col items-center justify-center">
      {/* Content */}
      <div className="flex flex-col gap-0 items-center justify-center px-0 py-[80px] relative w-full">
        <div className="flex flex-col gap-0 items-center max-w-sm md:max-w-lg px-[16px] py-0 relative shrink-0 w-full">
          {/* Card Container */}
          <div className="bg-background-primary flex flex-col items-start max-w-sm md:max-w-lg overflow-clip relative rounded-xl shadow-lg shrink-0 w-full">
            {/* Header with logo */}
            <div className="bg-foreground-brand-primary-600 flex flex-col items-center justify-center px-[24px] py-[20px] relative w-full">
              <div className="flex items-start relative w-[200px]">
                <div className="aspect-[596/159] flex-1 min-h-px min-w-px overflow-clip relative">
                  <img 
                    src="/Clip path group.svg" 
                    alt="Club 4X4" 
                    className="h-full w-auto object-contain brightness-0 invert"
                  />
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-[32px] items-start px-[24px] py-[40px] relative shrink-0 w-full">
              {/* Header */}
              <div className="flex flex-col gap-0 items-start relative shrink-0 w-full">
                <p className="font-display leading-[32px] relative shrink-0 text-text-primary-900 text-display-xs text-center w-full">
                  It's a match
                </p>
              </div>

              {/* Content */}
              <div className="flex flex-col gap-[24px] items-center rounded-lg relative shrink-0 w-full">
                {loading ? (
                  <LoadingIcon size="medium" message="Loading your policies..." />
                ) : error ? (
                  <>
                    <p className="font-body font-semibold text-md leading-md relative shrink-0 text-text-primary-900 w-full">
                      Unable to load policies
                    </p>
                    <p className="font-body font-regular text-md leading-md relative shrink-0 text-text-secondary-700 w-full">
                      {error}
                    </p>
                  </>
                ) : policies.length === 0 ? (
                  <p className="font-body font-regular text-md leading-md relative shrink-0 text-text-secondary-700 w-full">
                    No policies found
                  </p>
                ) : (
                  <>
                    <p className="font-body font-regular text-md leading-md relative shrink-0 text-text-secondary-700 w-full">
                      We found {policies.length} {policies.length === 1 ? 'policy' : 'policies'} that match your details:
                    </p>

                    {/* Policy Cards */}
                    <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                      {policies.map((policy, index) => (
                        <PolicyCard
                          key={index}
                          regoNumber={policy.regoNumber}
                          vehicleName={policy.vehicleName}
                          renewalDate={policy.renewalDate}
                        />
                      ))}
                    </div>

                    {/* Get Started Button */}
                    <PrimaryButton onClick={handleGetStarted} fullWidth>
                      Get started
                    </PrimaryButton>
                  </>
                )}
              </div>

              {/* Missing Policy Alert */}
              <div className="bg-background-primary border border-border-primary border-solid flex flex-col gap-[16px] items-start p-[16px] relative rounded-xl shadow-xs shrink-0 w-full">
                <div className="relative rounded-full shrink-0 size-[16.667px]">
                  <div className="absolute border-[1.667px] border-text-tertiary-600 border-solid inset-[-25%] opacity-30 rounded-2xl" />
                  <div className="absolute border-[1.667px] border-text-tertiary-600 border-solid inset-[-56.25%] opacity-10 rounded-2xl" />
                  <div className="absolute inset-0 overflow-clip">
                    <div className="absolute inset-[8.33%]">
                      <div className="absolute inset-[-4%] text-text-tertiary-600">
                        <InfoIcon className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                  <div className="flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                    <p className="font-body font-semibold text-sm leading-[24px] relative shrink-0 text-text-secondary-700 w-full">
                      Missing a policy?
                    </p>
                    <div className="font-body font-regular leading-0 relative shrink-0 text-text-tertiary-600 w-full">
                      <p className="font-body leading-[24px] mb-4 not-italic text-sm">
                        We can only link policies to your account if all your details match our records.
                      </p>
                      <p className="text-sm">
                        <span className="font-body font-regular leading-[24px] not-italic">
                          If you don't see your policy listed here,{' '}
                        </span>
                        <a href="#call-us" className="underline">
                          call us
                        </a>
                        <span className="font-body font-regular leading-[24px] not-italic">
                          {' '}to update your policy or make a claim.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </main>

      <Footer />
    </div>
  )
}

